/**
 * Menu management.
 *
 * The merchant's view of everything they sell: categories, dishes, photos,
 * prices, inventory, day-part availability, and time-boxed discounts.
 *
 * Writes go straight to the tables rather than through RPCs, because Row Level
 * Security already scopes every row to a restaurant the caller staffs and there
 * is no money to compute. The one server-side rule that does matter here —
 * which discount wins when several overlap — is applied at order time by
 * `private.best_discount_cents`, so the editor may show two live offers on one
 * dish and the customer still pays the better one.
 */

import { Fragment, useCallback, useMemo, useState } from 'react';
import { View } from 'react-native';
import { Modal, Pressable } from 'react-native';
import {
  ArrowUpDown,
  Camera,
  Check,
  ChevronDown,
  ChevronUp,
  FolderPlus,
  MoreHorizontal,
  Pencil,
  SquareCheck,
  Trash2,
  type LucideIcon,
} from 'lucide-react-native';
import { formatMoney, type MerchantMenu, type MerchantMenuItem } from '@yummy/shared';
import {
  deleteCategory,
  deleteMenuItem,
  deleteMenuItems,
  moveMenuItems,
  fetchMerchantMenu,
  fetchMerchantRestaurant,
  updateMenuItem,
} from '@yummy/db';
import {
  Button,
  Card,
  cn,
  Icon,
  ConfirmDialog,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  EmptyState,
  ErrorBanner,
  LoadingState,
  PageHeading,
  Row,
  Screen,
  SegmentedControl,
  Stack,
  Text,
  ToneBadge,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useAction,
  useAsync,
  useLocale,
  useToast,} from '@yummy/ui-rnr';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { FooterBar } from '../components/FooterBar';
import { useMerchant } from '../app/auth';
import { supabase } from '../lib/supabase';
import { LayoutGrid, UtensilsCrossed } from 'lucide-react-native';

/**
 * Renders the menu manager and its editors.
 *
 * @returns The menu screen.
 */
export function MenuScreen() {
  const { t } = useLocale();
  const toast = useToast();
  const navigate = useNavigate();
  const { restaurant } = useMerchant();
  const restaurantId = restaurant?.id ?? null;

  const menu = useAsync<MerchantMenu | null>(
    async () => (restaurantId ? fetchMerchantMenu(supabase, restaurantId) : null),
    [restaurantId],
  );

  const settings = useAsync(
    async () => (restaurantId ? fetchMerchantRestaurant(supabase, restaurantId) : null),
    [restaurantId],
  );

  const action = useAction();

  // In the URL rather than only in state: adding an item leaves this screen,
  // and coming back from the form should land on the category it was opened
  // from rather than on the whole menu.
  const [searchParams, setSearchParams] = useSearchParams();
  /**
   * Choosing several items to act on at once.
   *
   * A mode rather than a permanent column of checkboxes: the controls each row
   * already has are what a merchant wants most of the time, and a tick beside
   * a switch is two round green things where only one belongs.
   */

  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [moving, setMoving] = useState(false);
  /**
   * Setting the order dishes appear in.
   *
   * A mode, like Select, and for the same reason: the row's own controls are
   * what a merchant wants almost all of the time, and two arrows on every row
   * would be four more things to aim at on a screen that was deliberately
   * emptied of them. It is only offered for one list — a category, or the
   * unfiled dishes — because an order that runs across categories is not a
   * thing the customer menu has.
   */
  const [reordering, setReordering] = useState(false);

  const toggleSelected = useCallback((id: string) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const leaveSelecting = useCallback(() => {
    setSelecting(false);
    setSelected(new Set());
  }, []);

  /** Entering either mode leaves the other, so only one set of controls shows. */
  const startSelecting = useCallback(() => {
    setReordering(false);
    setSelecting(true);
  }, []);

  const startReordering = useCallback(() => {
    leaveSelecting();
    setReordering(true);
  }, [leaveSelecting]);

  const [categoryFilter, setCategoryFilter] = useState<string>(
    () => searchParams.get('category') ?? 'all',
  );
  const chooseCategory = useCallback(
    (id: string) => {
      setCategoryFilter(id);
      setSearchParams(id === 'all' ? {} : { category: id }, { replace: true });
    },
    [setSearchParams],
  );
  const [confirming, setConfirming] = useState<
    | { kind: 'item'; id: string; name: string }
    | { kind: 'category'; id: string; name: string }
  | { kind: 'items'; ids: string[]; count: number }
    | null
  >(null);

  const currency = settings.data?.currency ?? restaurant?.currency ?? 'SGD';

  // The filter, as something a form can be handed and give back. Every screen
  // this list opens — an item, a category, stock, the menu scanner — returns to
  // `/menu` with this on the query string, so Cancel puts a merchant back on the
  // tab they left rather than on All. `all` and `none` are filter words rather
  // than categories and are not passed: All has nothing to name, and the item
  // form reads this same parameter to decide which category to file a dish
  // under, where "none" is not a category it could save.
  const categoryQuery =
    categoryFilter !== 'all' && categoryFilter !== 'none' ? `?category=${categoryFilter}` : '';

  // Memoised on the fetched payload so the filter below does not re-run on
  // every render just because `?? []` produced a fresh array.
  const items = useMemo(() => menu.data?.items ?? [], [menu.data]);
  const categories = useMemo(() => menu.data?.categories ?? [], [menu.data]);

  const selectedCategory = categories.find((c) => c.id === categoryFilter);

  const visibleItems = useMemo(
    () =>
      categoryFilter === 'all'
        ? items
        : categoryFilter === 'none'
          ? items.filter((item) => item.category_id === null)
          : items.filter((item) => item.category_id === categoryFilter),
    [items, categoryFilter],
  );

  /**
   * The list, split by category when it is showing every category at once.
   *
   * Under All a flat list cannot say where a dish lives, and that is invisible
   * until something is moved: the rows chosen to be refiled carry no sign of
   * what they are leaving. Grouped, placement is a property of position rather
   * than something written on every row, which is the cheaper of the two — the
   * row's own line already carries the price and up to four state chips.
   *
   * A filter tab is one category already, so it stays flat: there is nothing
   * left to distinguish. A category with no dishes draws no heading, because a
   * heading over nothing reads as a broken list rather than as an empty
   * category; the tab strip above still counts it.
   */
  const groups = useMemo(() => {
    if (categoryFilter !== 'all') {
      return [{ key: categoryFilter, heading: null as string | null, items: visibleItems }];
    }

    const named = categories
      .map((category) => ({
        key: category.id,
        heading: category.name as string | null,
        items: visibleItems.filter((item) => item.category_id === category.id),
      }))
      .filter((group) => group.items.length > 0);

    const unfiled = visibleItems.filter((item) => item.category_id === null);
    return unfiled.length > 0
      ? [...named, { key: 'none', heading: t('merchant.menu.uncategorised'), items: unfiled }]
      : named;
  }, [categoryFilter, categories, visibleItems, t]);

  /** How many of these dishes the customer cannot see. */
  const hiddenCount = visibleItems.filter((item) => !item.is_active).length;

  /** `3 items` / `1 item` — a bare number beside a name reads as a length. */
  const countLabel = useCallback(
    (count: number) =>
      count === 1 ? t('merchant.menu.itemCountOne') : t('merchant.menu.itemCount', { count }),
    [t],
  );

  /**
   * Where the chosen dishes are filed, as `Mains 2, Sides 1`.
   *
   * Read from the list's own order — the categories as they are filed, then the
   * unfiled — rather than from the order they were ticked, so the line does not
   * reshuffle under the hand while a selection is being built.
   */
  const selectionOrigins = useMemo(() => {
    if (selected.size === 0) return [];

    const filedUnder = new Map(items.map((item) => [item.id, item.category_id]));
    const counts = new Map<string | null, number>();
    for (const id of selected) {
      const categoryId = filedUnder.get(id) ?? null;
      counts.set(categoryId, (counts.get(categoryId) ?? 0) + 1);
    }

    const inOrder: Array<{ id: string | null; name: string }> = [
      ...categories.map((category) => ({ id: category.id as string | null, name: category.name })),
      { id: null, name: t('merchant.menu.uncategorised') },
    ];

    return inOrder
      .filter((entry) => counts.has(entry.id))
      .map((entry) => ({
        id: entry.id,
        name: entry.name,
        count: counts.get(entry.id) ?? 0,
      }));
  }, [selected, items, categories, t]);

  /**
   * One destination in the move dialog.
   *
   * Flat rows inside one rounded outline, rather than six rounded buttons. A row
   * carries a hairline above it and nothing else — no side borders, no radius of
   * its own — so the group reads as a single list and the accent tint is the only
   * press state, the way every `Select` in the app draws its choices. The outline
   * is what keeps the group a single object: six boxed rows took 340px, being 50
   * each with 8 of empty space between them, where the same six flat rows take
   * 295px with their dividers, in a dialog that has to fit a phone.
   *
   * Three facts on the row: the category, how many of the chosen dishes already
   * live there, and how big it is. The middle one is what the dialog could not
   * say before — `Mains · 4 items` reads the same whether the selection is going
   * home or leaving. When every chosen dish is already there the row has nothing
   * to offer, so it says so rather than looking like the others.
   */
  const destination = (id: string | null, name: string) => {
    const already = selectionOrigins.find((origin) => origin.id === id)?.count ?? 0;
    const settled = already > 0 && already === selected.size;

    return (
      <Pressable
        key={id ?? 'none'}
        onPress={() => void moveSelected(id, name)}
        disabled={action.pending || settled}
        accessibilityLabel={name}
        className={cn(
          // A box each rather than a row of one grouped table. `overflow-hidden` is
          // the row's own now: the press tint has to be cut to this box's corners,
          // where it used to be cut by the single outline around the group.
          'border-border bg-card flex-row items-center justify-between gap-3 overflow-hidden rounded-lg border px-3 py-3',
          'active:bg-accent',
          settled && 'opacity-55',
        )}>
        <Text className="text-base">{name}</Text>
        <Row gap="sm" align="center" className="min-w-0 shrink">
          {already > 0 ? (
            <Text numberOfLines={1} className="text-primary-strong text-xs font-semibold">
              {settled
                ? t('merchant.menu.moveNothingToMove')
                : t('merchant.menu.moveAlreadyHere', { count: already })}
            </Text>
          ) : null}
          <Text className="text-subtle-foreground text-xs">
            {countLabel(items.filter((item) => item.category_id === id).length)}
          </Text>
        </Row>
      </Pressable>
    );
  };

  /**
   * One header action, as an icon.
   *
   * The same control the orders board puts in its own header — a 36px secondary
   * button, a 16px glyph, and a tooltip carrying the words the button no longer
   * has room for — so the two screens' headers read as the same kind of thing.
   * The label is still the accessible name, and still in the dictionaries: the
   * tooltip is where it is read, not where it lives.
   */
  const iconAction = (label: string, icon: LucideIcon, onPress: () => void) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="secondary"
          className="w-9 px-0"
          onPress={onPress}
          accessibilityLabel={label}>
          <Icon as={icon} aria-hidden className="size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <Text>{label}</Text>
      </TooltipContent>
    </Tooltip>
  );

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  /**
   * Puts moved items back where they came from.
   *
   * One call per distinct origin rather than one per item: a set moved from the
   * same category goes back together, which is what it was.
   */
  const undoMove = useCallback(
    async (previous: Map<string, string | null>) => {
      const byOrigin = new Map<string | null, string[]>();
      for (const [id, categoryId] of previous) {
        byOrigin.set(categoryId, [...(byOrigin.get(categoryId) ?? []), id]);
      }
      for (const [categoryId, ids] of byOrigin) {
        await moveMenuItems(supabase, ids, categoryId);
      }
      toast.show(t('merchant.menu.undoDone'), { tone: 'neutral' });
      menu.reload();
    },
    [menu, toast, t],
  );

  const moveSelected = useCallback(
    async (categoryId: string | null, categoryName: string) => {
      const ids = [...selected];
      // Where they were, captured before the move: a bulk act taken by mistake
      // is a dozen taps to put back by hand.
      const previous = new Map(
        ids.map((id) => [id, items.find((i) => i.id === id)?.category_id ?? null]),
      );

      const moved = await action.run(() => moveMenuItems(supabase, ids, categoryId));
      if (moved !== null) {
        toast.show(
          ids.length === 1
            ? t('merchant.menu.moveDoneOne', { category: categoryName })
            : t('merchant.menu.moveDone', { count: ids.length, category: categoryName }),
          {
            tone: 'success',
            actionLabel: t('merchant.menu.undo'),
            onAction: () => void undoMove(previous),
          },
        );
        setMoving(false);
        leaveSelecting();
        menu.reload();
      }
    },
    [selected, items, action, toast, t, leaveSelecting, menu, undoMove],
  );

  /**
   * Moves one dish a step up or down the list it is in.
   *
   * The whole visible list is renumbered rather than the two rows swapping
   * their numbers: a category's dishes do not have to be distinct — Breakfast
   * has two sharing a sort order — and swapping two equal numbers would move
   * nothing. Renumbering from the list's own order leaves every dish with a
   * position of its own, which is also what makes the next press unambiguous.
   *
   * `sort_order` is the column the customer menu aggregates by, so this is the
   * order customers read, not a private preference of the merchant's.
   */
  const moveWithinList = useCallback(
    async (item: MerchantMenuItem, direction: -1 | 1) => {
      const order = visibleItems.map((candidate) => candidate.id);
      const from = order.indexOf(item.id);
      const to = from + direction;
      if (from < 0 || to < 0 || to >= order.length) return;

      order[from] = order[to] as string;
      order[to] = item.id;

      const saved = await action.run(() =>
        Promise.all(order.map((id, index) => updateMenuItem(supabase, id, { sort_order: index }))),
      );
      if (saved) menu.reload();
    },
    [visibleItems, action, menu],
  );

  const confirmDelete = useCallback(async () => {
    if (!confirming) return;

    const result = await action.run(() =>
      confirming.kind === 'items'
        ? deleteMenuItems(supabase, confirming.ids)
        : confirming.kind === 'item'
          ? deleteMenuItem(supabase, confirming.id)
          : deleteCategory(supabase, confirming.id),
    );

    if (result !== null || !action.error) {
      toast.show(
        confirming.kind === 'items'
          ? t('merchant.menu.deletedCount', { count: confirming.count })
          : t('merchant.menu.deleted', { name: confirming.name }),
        { tone: 'neutral' },
      );
      // A deleted category leaves the filter pointing at something that no
      // longer exists: no tab selected, an empty list, and a heading of
      // "Menu" over nothing. Its items went to Uncategorised, so the whole
      // menu is the honest place to be.
      if (confirming.kind === 'category') {
        setCategoryFilter('all');
        setSearchParams({}, { replace: true });
      }
      // Nothing selected survives being deleted.
      if (confirming.kind === 'items') leaveSelecting();
      setConfirming(null);
      menu.reload();
    }
  }, [confirming, action, menu, toast, t, setSearchParams, leaveSelecting]);

  const toggleActive = useCallback(
    async (item: MerchantMenuItem) => {
      const result = await action.run(() =>
        updateMenuItem(supabase, item.id, { is_active: !item.is_active }),
      );
      if (result) {
        menu.reload();
        toast.show(
          result.is_active
            ? t('merchant.menu.nowOnMenu', { name: result.name })
            : t('merchant.menu.nowHidden', { name: result.name }),
          {
            tone: 'neutral',
          },
        );
      }
    },
    [action, menu, toast, t],
  );

  // ---------------------------------------------------------------------------

  if (!restaurantId) {
    return (
      <Screen>
        <EmptyState icon={UtensilsCrossed} title={t('merchant.menu.noRestaurant')} />
      </Screen>
    );
  }

  if (menu.initial) {
    return (
      <Screen>
        <LoadingState message={t('merchant.menu.loading')} />
      </Screen>
    );
  }

  if (menu.error) {
    return (
      <Screen>
        <ErrorBanner message={menu.error} onRetry={menu.reload} />
      </Screen>
    );
  }

  return (
    <Screen constrain={false} footer={
        selecting && selected.size > 0 ? (

          <FooterBar>
            <Row spread gap="md" align="center">
            {/* What is chosen, and where it comes from. The count alone cannot
                say that a selection spans categories, which is the whole
                question when the list is All — and by the time the footer is in
                view the headings above it may have scrolled away. Under a
                category tab every row is that category, so the origins are left
                out there rather than repeating the tab. */}
            <Row gap="sm" align="center" className="min-w-0 shrink">
              <Text className="text-muted-foreground text-sm font-semibold">
                {t('merchant.menu.selectedCount', { count: selected.size })}
              </Text>
              {categoryFilter === 'all' && selectionOrigins.length > 0 ? (
                /* The same size as the count beside it, not a size down. The
                   two are one line of text and the eye aligns them by their
                   baselines, which two different sizes cannot do while their
                   boxes are centred — and this one is clamped to a line, so the
                   alignment cannot be asked of the browser either. Weight and
                   colour carry the difference instead. */
                <Text numberOfLines={1} className="text-subtle-foreground text-sm">
                  {`· ${selectionOrigins
                    .map((origin) => `${origin.name} ${origin.count}`)
                    .join(', ')}`}
                </Text>
              ) : null}
            </Row>
            {/* Grouped rather than left as three children of a spread row:
                with three, the row spaces all of them apart and Delete ends up
                stranded in the middle. */}
            <Row gap="sm" align="center">
              <Button
                size="sm"
                variant="secondary"
                onPress={() =>
                  setConfirming({ kind: 'items', ids: [...selected], count: selected.size })
                }
                accessibilityLabel={t('common.delete')}>
                <Text>{t('common.delete')}</Text>
              </Button>

            <Button
              size="sm"
              onPress={() => setMoving(true)}
                accessibilityLabel={t('merchant.menu.moveTo')}>
                <Text>{t('merchant.menu.moveTo')}</Text>
              </Button>
            </Row>
            </Row>
          </FooterBar>
        ) : null
      }>
      <Stack gap="lg">
        {/* One accent button on screen. With a category chosen this is that
            category's action — the same act the header used to offer, moved to
            where the other actions are and named for where it adds. */}
        <PageHeading
          // The heading describes what is below it. Saying "Menu · 18 items" over
          // a list of six reads as a bug, so both follow the filter.
          title={selectedCategory?.name ?? t('merchant.menu.title')}
          // What the list holds, in every mode. Reorder says what it is by the
          // arrows on the rows and the Done beside this heading.
          subtitle={`${countLabel(visibleItems.length)}${
            hiddenCount > 0 ? ` · ${t('merchant.menu.hiddenCount', { count: hiddenCount })}` : ''
          }`}
          // 180, not the 240 the other screens use: this screen's action row is
          // five controls wide, and 240 of floor plus those five needed 526px of
          // a 484px column at the narrowest tablet width.
          minWidth={180}
          action={
            <Row gap="sm" wrap>
              {selecting || reordering ? (
                <Button
                  variant="secondary"
                  onPress={selecting ? leaveSelecting : () => setReordering(false)}
                  accessibilityLabel={t('merchant.menu.selectDone')}>
                  <Text>{t('merchant.menu.selectDone')}</Text>
                </Button>
              ) : visibleItems.length > 0 ? (
                <>
                  {iconAction(t('merchant.menu.select'), SquareCheck, startSelecting)}
                  {/* Only where there is a single list to order — a category, or
                      the unfiled dishes. Under All the order runs across the
                      headings, and the customer menu has no order like that. */}
                  {categoryFilter !== 'all' && visibleItems.length > 1
                    ? iconAction(t('merchant.menu.reorder'), ArrowUpDown, startReordering)
                    : null}
                </>
              ) : null}
              {/* One label, whatever the category is called. Naming the category
                  in the button made its width a property of the data — a dish
                  added to `Mains` and one added to `Signature Grilled Skewers`
                  drew different buttons — which in turn decided where the header
                  wrapped. The category is on the heading beside it and in the tab
                  above it, and the form shows the picker, so the words here say
                  only what the button does. */}
              <Button
                // Nothing to file a dish under yet. The screen behind this says
                // to make a category, and offering a button that leads to a form
                // with an empty picker contradicts it.
                disabled={categories.length === 0}
                onPress={() => navigate(`/menu/items/new${categoryQuery}`)}
                accessibilityLabel={t('merchant.menu.newItem')}>
                <Text>{t('merchant.menu.newItem')}</Text>
              </Button>
              {/* The two that happen once, when the restaurant is set up. They
                  sit after the button a merchant presses every day, and the two
                  modes — Select and Reorder — sit before it, because those are
                  the ones reached for while working. All four are the same weight
                  now: an icon button says what it is by its glyph, not by how
                  loud it is. */}
              {iconAction(t('merchant.menu.newCategory'), FolderPlus, () =>
                navigate(`/menu/categories/new${categoryQuery}`),
              )}
              {iconAction(t('merchant.menu.scanTitle'), Camera, () =>
                navigate(`/menu/scan${categoryQuery}`),
              )}
            </Row>
          }
        />

        {action.error ? <ErrorBanner message={action.error} onRetry={action.clearError} /> : null}

        <SegmentedControl
          options={[
            { value: 'all', label: t('common.all'), badge: items.length },
            ...categories.map((category) => ({
              value: category.id,
              label: category.name,
              badge: items.filter((i) => i.category_id === category.id).length,
            })),
            ...(items.some((i) => i.category_id === null)
              ? [{ value: 'none', label: t('merchant.menu.uncategorised'), badge: items.filter((i) => i.category_id === null).length }]
              : []),
          ]}
          value={categoryFilter}
          onChange={chooseCategory}
        />

        {categoryFilter !== 'all' && categoryFilter !== 'none' ? (
          <CategoryAdmin
            category={categories.find((c) => c.id === categoryFilter)}
            onRename={() => {
              const found = categories.find((c) => c.id === categoryFilter);
              if (found) navigate(`/menu/categories/${found.id}${categoryQuery}`);
            }}
            onDelete={() => {
              const found = categories.find((c) => c.id === categoryFilter);
              if (found) setConfirming({ kind: 'category', id: found.id, name: found.name });
            }}
          />
        ) : null}

        {categories.length === 0 ? (
          <EmptyState
            icon={LayoutGrid}
            title={t('merchant.menu.emptyNoCategories')}
            message={t('merchant.menu.emptyNoCategoriesHint')}
            actionLabel={t('merchant.menu.newCategory')}
            onAction={() => navigate(`/menu/categories/new${categoryQuery}`)}
          />
        ) : visibleItems.length === 0 ? (
          <EmptyState
            icon={UtensilsCrossed}
            title={t('merchant.menu.empty')}
            message={t('merchant.menu.emptyHint')}
            actionLabel={t('merchant.menu.newItem')}
            onAction={() => navigate(`/menu/items/new${categoryQuery}`)}
          />
        ) : (
          <Card className="gap-0 overflow-hidden p-0">
            {groups.map((group, groupIndex) => (
              <Fragment key={group.key}>
                {/* The heading a dish is filed under. Hairlines still do the
                    dividing — the band between two groups is the same one
                    point rule, in the muted tone the tab strip uses, rather
                    than a second kind of separator. */}
                {group.heading ? (
                  <Row
                    spread
                    gap="md"
                    align="center"
                    className={cn(
                      'bg-muted px-3 py-2',
                      groupIndex > 0 && 'border-border border-t',
                    )}>
                    <Text className="text-muted-foreground text-[11px] font-bold tracking-[0.06em] uppercase">
                      {group.heading}
                    </Text>
                    <Text className="text-subtle-foreground text-[11px]">
                      {countLabel(group.items.length)}
                    </Text>
                  </Row>
                ) : null}
                {group.items.map((item, index) => {
              const liveDiscount = item.discounts.find((d) => d.is_running_now);
              // Flush rows divided by a hairline: a border around each one and a
              // gap between them reads as a stack of cards, which is what the
              // list was meant to stop being. The last row of a group gives up
              // its rule, because the next group's heading carries one.
              const first = index === 0;
              const last = index === group.items.length - 1;
              return (
                <Pressable
                  key={item.id}
                  // The row is not a target. Every command on a dish — Edit
                  // included — is behind the one button at the end of the row, so
                  // a press along the name or the price does nothing rather than
                  // opening the editor on the way to somewhere else. Select mode
                  // is the exception, where the whole row is the checkbox it
                  // already looks like; the action button is gone by then, so the
                  // two never share a target.
                  onPress={selecting ? () => toggleSelected(item.id) : undefined}
                  className={cn(
                    'px-3 py-2.5',
                    !last && 'border-border border-b',
                    selecting && selected.has(item.id) && 'bg-muted',
                  )}>
                  <Row gap="md" align="center">
                    {selecting ? (
                      <View
                        className={cn(
                          'h-5 w-5 items-center justify-center rounded border',
                          selected.has(item.id) ? 'border-primary bg-primary' : 'border-input',
                        )}>
                        {selected.has(item.id) ? (
                          <Icon as={Check} size={12} aria-hidden className="text-primary-foreground" />
                        ) : null}
                      </View>
                    ) : null}
                    {/* No thumbnail, with or without a photo on the dish. The
                        list is read for the name, the price and the state
                        badges; a 44-point tile in front of every one of them
                        was a column of colour competing with that, and the
                        editor still shows the picture where it is being
                        chosen. */}
                    <Stack gap="xs" className="min-w-0 flex-1">
                      <Text numberOfLines={1} className="text-base font-semibold">
                        {item.name}
                      </Text>
                      {/* The price and its state share a line, so a dish is two
                          lines whatever is true of it. Given one line each, the
                          badges on their own made every discounted or tracked
                          dish a line taller than the rest — and the eye reading
                          down the prices had to come back up for the reason.
                          `wrap` is what happens when they do not all fit: the
                          chips take a second line as a group rather than being
                          cut off, which on a narrow window is a row of three
                          lines again. Order is fixed — hidden, offer, stock,
                          schedule — so a fact keeps its place down the list.

                          Every chip carries `self-center` because ToneBadge sets
                          `self-start`: a badge is usually the only thing in its
                          column and belongs at the top of it, but here it sits
                          beside the price, and left to itself it hangs off the
                          price's line by several points. Aligning the row's
                          baselines instead does not help — the chip's label is
                          clamped to one line, which leaves the box no text
                          baseline to offer, so the browser falls back to its
                          bottom edge and lifts the chip higher still. */}
                      <Row gap="sm" align="center" wrap>
                        {/* The price is a step down from the name — 14 rather
                            than 16, weight 500 rather than 600, muted rather
                            than full contrast — because on this row the two are
                            neighbours: the merchant's list has no description
                            between them, so set alike they read as two dishes
                            rather than a dish and its price. It is the cart's
                            treatment (`text-muted-foreground text-sm`) with one
                            step of weight added to hold its own beside an 11px
                            chip, and it keeps tabular figures so thirty prices
                            line up down the column.

                            Rendered here rather than through `Price`, which
                            sets its own colour and size on the text and so
                            cannot be asked for the quiet variant without
                            changing the component both apps share. The customer
                            menu row renders its price range the same way. */}
                        <Text className="text-muted-foreground text-sm font-medium tabular-nums">
                          {formatMoney(item.price_cents, currency)}
                        </Text>
                        {!item.is_active ? (
                          <ToneBadge
                            className="self-center"
                            label={t('merchant.menu.hidden')}
                            tone="neutral"
                            size="sm"
                          />
                        ) : null}
                        {liveDiscount ? (
                          <ToneBadge
                            className="self-center"
                            label={
                              liveDiscount.kind === 'percent'
                                ? t('merchant.menu.offPercent', { value: liveDiscount.value })
                                : t('merchant.menu.offAmount', {
                                    value: formatMoney(liveDiscount.value, currency),
                                  })
                            }
                            tone="brand"
                            emphasis="solid"
                            size="sm"
                          />
                        ) : null}
                        {item.track_inventory ? (
                          <ToneBadge
                            className="self-center"
                            label={t('merchant.menu.left', { count: item.inventory_count ?? 0 })}
                            tone={(item.inventory_count ?? 0) > 0 ? 'info' : 'danger'}
                            size="sm"
                          />
                        ) : null}
                        {item.availability_windows.length > 0 ? (
                          <ToneBadge
                            className="self-center"
                            label={t('merchant.menu.scheduledBadge')}
                            tone="progress"
                            size="sm"
                          />
                        ) : null}
                      </Row>
                    </Stack>

                    {/* One button, and every command behind it — the only thing
                        on the row that can be pressed. The switch that used to
                        sit here made hiding a dish a one-tap act on a screen
                        being scanned, but at the cost of a control on every row
                        that had nothing to do with the dish's name or price.
                        The commands are ordered by how often they are reached
                        for: the visibility toggle first, the dish's own screens
                        together, and Delete alone below a rule, because it is
                        the one that cannot be taken back.

                        The name and the price beside it are inert, so this
                        button shares its target with nothing. */}
                    {selecting ? null : reordering ? (
                    /* Two arrows instead of the menu. The ends of the list lose
                       the press they would do nothing with, rather than being
                       hidden: a control that comes and goes as a dish moves is
                       one more thing to keep track of under the hand. */
                    <Row gap="xs" align="center">
                      <Pressable
                        onPress={() => void moveWithinList(item, -1)}
                        disabled={first || action.pending}
                        accessibilityLabel={t('merchant.menu.reorderUp', { name: item.name })}
                        className={cn(
                          'border-border h-9 w-9 items-center justify-center rounded-md border active:opacity-70',
                          first && 'opacity-40',
                        )}>
                        <Icon as={ChevronUp} size={16} aria-hidden className="text-muted-foreground" />
                      </Pressable>
                      <Pressable
                        onPress={() => void moveWithinList(item, 1)}
                        disabled={last || action.pending}
                        accessibilityLabel={t('merchant.menu.reorderDown', { name: item.name })}
                        className={cn(
                          'border-border h-9 w-9 items-center justify-center rounded-md border active:opacity-70',
                          last && 'opacity-40',
                        )}>
                        <Icon as={ChevronDown} size={16} aria-hidden className="text-muted-foreground" />
                      </Pressable>
                    </Row>
                    ) : (
                    <Row gap="xs" align="center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Pressable
                            accessibilityRole="button"
                            accessibilityLabel={t('merchant.menu.actionsFor', { name: item.name })}
                            className="border-border h-9 w-9 items-center justify-center rounded-md border active:opacity-70">
                            <Icon
                              as={MoreHorizontal}
                              size={16}
                              aria-hidden
                              className="text-muted-foreground"
                            />
                          </Pressable>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[12rem] rounded-[10px] p-[5px]">
                          {/* A label rather than a disabled item: it names the
                              menu, and there is nothing to choose in it. It is
                              set as a section heading — small, spaced and
                              quiet — so it cannot be mistaken for the first
                              thing to press, which is what a line of body text
                              above a rule reads as.

                              The entries carry their own size and colour
                              rather than inheriting the item's: a `Text` inside
                              one of these sets its own, and the item's
                              `text-destructive` never reaches it. */}
                          <DropdownMenuLabel className="border-border mb-[5px] border-b">
                            <Text className="text-subtle-foreground text-[11px] font-bold uppercase tracking-[0.06em]">
                              {t('common.actions')}
                            </Text>
                          </DropdownMenuLabel>

                          <DropdownMenuItem onPress={() => void toggleActive(item)}>
                            <Text className="text-sm">
                              {item.is_active
                                ? t('merchant.menu.hideFromCustomers')
                                : t('merchant.menu.showToCustomers')}
                            </Text>
                          </DropdownMenuItem>

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            onPress={() => navigate(`/menu/items/${item.id}${categoryQuery}`)}>
                            <Text className="text-sm">{t('common.edit')}</Text>
                          </DropdownMenuItem>

                          {/* Only a dish that counts portions has stock to
                              count, so the entry is absent rather than disabled
                              on the rest. */}
                          {item.track_inventory ? (
                            <DropdownMenuItem
                              onPress={() =>
                                navigate(`/menu/items/${item.id}/stock${categoryQuery}`)
                              }>
                              <Text className="text-sm">{t('merchant.menu.stock')}</Text>
                            </DropdownMenuItem>
                          ) : null}

                          <DropdownMenuSeparator />

                          <DropdownMenuItem
                            variant="destructive"
                            onPress={() =>
                              setConfirming({ kind: 'item', id: item.id, name: item.name })
                            }>
                            <Text className="text-destructive text-sm">{t('common.delete')}</Text>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Row>
                    )}
                  </Row>
                </Pressable>
              );
                })}
              </Fragment>
            ))}
          </Card>
        )}

        {/* Appears only with something chosen, so the screen is unchanged
            until a merchant asks for the mode. */}
      </Stack>

      {/* ------------------------------------------------------------------ */}
      {/* Confirmation                                                       */}
      {/* ------------------------------------------------------------------ */}

      <Modal visible={moving} transparent animationType="fade" onRequestClose={() => setMoving(false)}>
        <View className="bg-foreground/40 flex-1 items-center justify-center p-4">
          <Card className="w-full max-w-md gap-3 p-4">
            <Stack gap="xs">
              <Text className="text-lg font-semibold">
                {selected.size === 1
                  ? t('merchant.menu.moveTitleOne')
                  : t('merchant.menu.moveTitle', { count: selected.size })}
              </Text>
              {/* What is being carried, as the dialog's own sentence rather than
                  a line of text in a box. The box was 38px and a 12px gap on a
                  dialog that has to fit a phone, for one short line — and it read
                  as a third kind of thing on a screen that has a heading and a
                  list. The list below counts each category's own contents, so
                  without this the dialog reads the same whether the selection is
                  going home or leaving.

                  The sentence that used to sit here — that a dish lands at the
                  end of the category — is about what happens *after* the move,
                  so it belongs to the toast, which is the only moment the result
                  exists. */}
              {selectionOrigins.length > 0 ? (
                <Text numberOfLines={2} className="text-muted-foreground text-sm">
                  {`${t('merchant.menu.moveFrom')} `}
                  {/* Names only. The count of each origin is not what this line
                      answers — how many are going, and where each destination
                      stands, are both on the list below — and it doubled the
                      width of a line that has to hold as many categories as the
                      merchant cares to select from.

                      `text-sm` is repeated rather than inherited: `Text` sets
                      its own size, so without it these render at the base 16
                      against the 14 of the word beside them. */}
                  <Text className="text-foreground text-sm font-medium">
                    {selectionOrigins.map((origin) => origin.name).join(' · ')}
                  </Text>
                </Text>
              ) : null}
            </Stack>

            {/* A grey tray holding a box each, which is the shape the kit already
                uses for a small set of mutually exclusive things — a muted track
                with the choices standing on it. One outline around the group made
                the destinations read as rows of a single table, one thing with
                parts, when each is a place the selection can go: the tray says what
                the set is and the gaps say they are separate, without a word. The
                rows keep the app's card treatment inside it, so the box is white on
                the tray rather than a second kind of surface. */}
            <View className="bg-muted rounded-xl p-2">
              <Stack gap="sm">
                {categories.map((category) => destination(category.id, category.name))}
                {destination(null, t('merchant.menu.uncategorised'))}
              </Stack>
            </View>

            <Row gap="sm" justify="end">
              <Button variant="ghost" onPress={() => setMoving(false)} accessibilityLabel={t('common.cancel')}>
                <Text>{t('common.cancel')}</Text>
              </Button>
            </Row>
          </Card>
        </View>
      </Modal>

      <ConfirmDialog
        visible={confirming !== null}
        title={
          confirming?.kind === 'items'
            ? confirming.count === 1
              ? t('merchant.menu.deleteItemsTitleOne')
              : t('merchant.menu.deleteItemsTitle', { count: confirming.count })
            : confirming
              ? t('merchant.menu.deleteItemTitle', { name: confirming.name })
              : ''
        }
        message={
          confirming?.kind === 'category'
            ? t('merchant.menu.deleteCategoryMessage')
            : confirming?.kind === 'items'
              ? t('merchant.menu.deleteItemsMessage', { count: confirming.count })
              : t('merchant.menu.deleteItemMessage')
        }
        confirmLabel={t('common.delete')}
        destructive
        busy={action.pending}
        onConfirm={() => void confirmDelete()}
        onCancel={() => setConfirming(null)}
      />
    </Screen>
  );
}

// ---------------------------------------------------------------------------
// Category admin strip
// ---------------------------------------------------------------------------

/**
 * The rename, delete, and add controls for the selected category.
 *
 * @param props The category and its handlers.
 * @returns The category strip, or nothing when the category is gone.
 */
function CategoryAdmin({
  category,
  onRename,
  onDelete,
}: {
  category?: { name: string; description: string | null };
  onRename: () => void;
  onDelete: () => void;
}) {
  const { t } = useLocale();
  if (!category) return null;

  return (
    <Card className="p-3">
      <Row spread gap="md" align="center" wrap>
        <Stack gap="xs" className="min-w-[200px] flex-1">
          <Text className="text-lg font-semibold">{category.name}</Text>
          {category.description ? (
            <Text className="text-muted-foreground text-sm">{category.description}</Text>
          ) : null}
        </Stack>

        {/* The same language as the rows below. Adding an item is what a
            merchant most often came here to do, so it keeps its words and the
            accent; renaming and deleting change the category itself and are
            icons. Three worded buttons of equal weight made the header look
            like three equally likely choices. */}
        <Row gap="xs" align="center">
          <Pressable
            onPress={onRename}
            accessibilityLabel={t('merchant.menu.categoryRename')}
            className="border-border h-9 w-9 items-center justify-center rounded-md border active:opacity-70">
            <Icon as={Pencil} size={15} aria-hidden className="text-muted-foreground" />
          </Pressable>

          <Pressable
            onPress={onDelete}
            accessibilityLabel={t('common.delete')}
            className="border-border h-9 w-9 items-center justify-center rounded-md border active:opacity-70">
            <Icon as={Trash2} size={15} aria-hidden className="text-destructive" />
          </Pressable>
        </Row>
      </Row>
    </Card>
  );
}
